import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { Typography } from '@mui/material';
import { useStore } from '../../../hooks/useStore';
import { Chip, ContentHead } from '../../../lib/components';
import { ContentBox } from '../../../lib/components/compound/pages/contentBox';
import { CheckmarkIcon, CloseIcon } from '../../../lib/icons';
import { TableDataGrid } from '../../../lib/components/compound/tableDataGrid';
import { GridCellParams, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { EmployeeBenefitBudget } from '@pxtra-client-lib-legacy/gen';
import { useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { useDate } from '../partials/profilePagesPartial';
import { useParams } from 'react-router';
import {} from 'i18next';
import Util from '../../../misc/util';
import { BenefitCell } from '../../partials/table';
import { Benefit } from '@pxtra-client-lib-legacy/types';

export const EmployeeBenefitsOverview = observer(() => {
  const { employeeStore, companyStore } = useStore();
  const { employeeId } = useParams();
  const { t } = useTranslation();
  const [date] = useDate();

  type Row = {
    custom?: boolean;
    id: Benefit | string;
    value: number;
    desired: boolean | undefined;
    status: boolean;
    benefit: string;
    icon?: string;
  };

  const settings = employeeStore.currentEmployee?.benefit_settings;
  const budget = employeeStore.currentEmployee?.benefit_budget;
  const companySettings = companyStore.benefitSettings;

  const rows = useMemo(() => {
    if (!settings || !budget || !companySettings) return [];

    const keys = Util.getKeys(settings);
    const rows: Row[] = keys.map((key) => {
      return {
        id: key,
        value: (budget[key as keyof EmployeeBenefitBudget] as number) || 0,
        desired: settings[key as Benefit].desired,
        status: budget.active_benefits?.includes(key) ?? false,
        benefit: key
      };
    });

    budget.custom?.forEach((customBudget) => {
      rows.push({
        custom: true,
        id: customBudget.id,
        value: customBudget.amount,
        desired: false,
        status: true,
        benefit: customBudget.name,
        icon: customBudget.icon
      });
    });

    return rows;
  }, [settings, budget, companySettings]);

  const columns = useMemo(
    () => [
      {
        field: 'benefit',
        headerName: 'Benefit',
        flex: 2,
        renderCell: (params: GridRenderCellParams) => (
          <BenefitCell
            variant="contained"
            benefitKey={params.row.id}
            benefitIcon={params.row.icon}
            benefitName={params.row.benefit}
            custom={params.row.custom}
          />
        )
      },
      {
        field: 'value',
        headerName: t('common:VALUE'),
        description: 'Anteiliger Wert des Benefits',
        minWidth: 100,
        type: 'number',
        renderCell: (params: GridCellParams) => (
          <Typography variant="subtitle2">{params.value}</Typography>
        ),
        valueGetter: ({ value: unformatted }: { value: number }) => {
          return Util.formatCurrency(unformatted);
        }
      },
      {
        field: 'desired',
        headerName: t('common:DESIRE'),
        description: 'Ist der Benefit gewünscht',
        width: 80,
        renderCell: (params: GridCellParams) => {
          return params.value ? (
            <CheckmarkIcon
              color="white"
              sx={{
                backgroundColor: 'success.main',
                borderRadius: '50%'
              }}
            />
          ) : (
            <CloseIcon
              color="white"
              sx={{
                backgroundColor: 'grey.100',
                borderRadius: '50%'
              }}
            />
          );
        }
      },
      {
        field: 'status',
        headerName: 'Status',
        description: 'Status des Benefits',
        width: 104,
        renderCell: (params: GridCellParams) => {
          return (
            <Chip
              disabled
              color={params.value ? 'heaven' : 'neutral'}
              label={params.value ? t('employee:ACTIVE') : t('employee:INACTIVE')}
              sx={{
                opacity: params.value ? '1 !important' : '.3 !important',
                width: '70px'
              }}
            />
          );
        }
      }
    ],
    []
  );

  useEffect(() => {
    const fn = async () => {
      if (date && employeeId) {
        await employeeStore.fetchEmployeeBudgetInformation(employeeId, format(date, 'yyyy-MM-dd'));
        companyStore.fetchBenefitSettings();
      }
    };
    fn();
  }, [date]);

  return (
    <ContentBox
      headComponent={<ContentHead headline={t('common:BENEFIT_OVERVIEW')} showActions={false} />}
    >
      <TableDataGrid
        columns={columns}
        rows={rows}
        checkboxSelection={false}
        autoHeight={rows.length > 0}
        hideFooter
        hideToolbar
      />
    </ContentBox>
  );
});
